import { defineStore } from 'pinia'
import { fetchBookingToken } from '../services/oauthService'

interface TokenInfo {
  token: string
  expirationTime: number
  type: 'public' | 'booking' | 'crm'
  bookingNumber?: string
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    currentToken: null as TokenInfo | null,
  }),
  getters: {
    token: (state) => state.currentToken?.token || null,
    tokenExpirationTime: (state) => state.currentToken?.expirationTime || null,
    isTokenValid: (state) => {
      if (!state.currentToken) return false
      return Date.now() < state.currentToken.expirationTime
    },
    isBookingToken: (state) => state.currentToken?.type === 'booking',
    isCrmToken: (state) => state.currentToken?.type === 'crm',
    currentBookingNumber: (state) => state.currentToken?.bookingNumber || null,
  },
  actions: {
    setToken(
      token: string,
      expirationTime: number,
      type: 'public' | 'booking' | 'crm' = 'public',
      bookingNumber?: string
    ) {
      this.currentToken = {
        token,
        expirationTime,
        type,
        bookingNumber,
      }
    },
    setBookingTokenFromLocalStorage() {
      const token = localStorage.getItem('token')
      const bookingNumber = localStorage.getItem('pendingBookingNumber')
      if (token && bookingNumber) {
        const expirationTime = Date.now() + 3600000 // Token expires in 1 hour
        this.setToken(token, expirationTime, 'booking', bookingNumber)
      }
    },
    clearToken() {
      if (this.isBookingToken && this.currentBookingNumber) {
        localStorage.removeItem(`email_${this.currentBookingNumber}`)
      }
      this.currentToken = null
    },
    async getBookingToken(
      bookingNumber: string,
      email: string
    ): Promise<string> {
      try {
        const token = await fetchBookingToken(bookingNumber, email)
        // Set expiration to 20 minutes from now
        const expirationTime = Date.now() + 20 * 60 * 1000
        this.setToken(token, expirationTime, 'booking', bookingNumber)
        localStorage.setItem(`email_${bookingNumber}`, email)
        return token
      } catch (error) {
        console.error('Failed to get booking token:', error)
        throw error
      }
    },
    
    async findAndUseEmailForBooking(bookingNumber: string): Promise<string | null> {
      let email = localStorage.getItem(`email_${bookingNumber}`)
      
      if (!email && localStorage.getItem('pendingBookingNumber') === bookingNumber) {
        const allKeys = Object.keys(localStorage)
        const emailKeys = allKeys.filter(key => key.startsWith('email_'))
        
        if (emailKeys.length > 0) {
          const mostRecentEmailKey = emailKeys[0]
          const fallbackEmail = localStorage.getItem(mostRecentEmailKey)
          
          if (fallbackEmail) {
            localStorage.setItem(`email_${bookingNumber}`, fallbackEmail)
            email = fallbackEmail
          }
        }
      }
      
      if (email) {
        try {
          const token = await this.getBookingToken(bookingNumber, email)
          return token
        } catch (tokenError) {
          console.warn('Failed to get booking token with email:', tokenError)
        }
      }
      
      return null
    },
    async refreshBookingToken(): Promise<boolean> {
      if (this.isBookingToken && this.currentBookingNumber) {
        const email = localStorage.getItem(`email_${this.currentBookingNumber}`) || ''
        if (email) {
          try {
            const token = await fetchBookingToken(this.currentBookingNumber, email)
            // Set expiration to 20 minutes from now
            const expirationTime = Date.now() + 20 * 60 * 1000
            this.setToken(token, expirationTime, 'booking', this.currentBookingNumber)
            console.log('Refreshed booking token for booking:', this.currentBookingNumber)
            return true
          } catch (error) {
            console.error('Failed to refresh booking token:', error)
            return false
          }
        }
      }
      return false
    },
  },
})
